import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useParams } from 'react-router-dom';
import {
  Button, Grid, Header, Icon, Image, Label, Message,
  Segment, Tab, TabPane
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import LoadingComponent from '../../app/layout/LoadingComponent';
import TeamChatPanel from './TeamChatPanel';
import { format } from 'date-fns';
import { TeamMatchAvailabilityPanel, TeamMatchResultPanel } from './TeamMatchPanels';

export default observer(function TeamMatchPage() {
  const { id } = useParams<{ id: string }>();
  const { teamMatchStore } = useStore();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const { selectedMatch, loadingInitial, loadMatch, submitting,
    respondToChallenge, acceptApplication } = teamMatchStore;

  useEffect(() => {
    if (id) loadMatch(id);
    return () => teamMatchStore.clearSelectedMatch();
  }, [id]);

  if (loadingInitial || !selectedMatch) return <LoadingComponent content={t('teams.loading')} />;

  const match = selectedMatch;

  const statusColor: Record<string, 'yellow' | 'green' | 'red' | 'grey' | 'blue' | 'teal' | 'orange'> = {
    Pending: 'yellow',
    Accepted: 'green',
    Rejected: 'red',
    Open: 'blue',
    TeamApplied: 'orange',
    Scheduled: 'teal',
    Completed: 'green',
    Cancelled: 'grey',
  };

  const isMember = match.isCurrentUserTeamA || match.isCurrentUserTeamB;

  const panes = [
    ...(match.status !== 'Pending' && match.status !== 'Rejected' ? [{
      menuItem: { key: 'chat', content: t('teamMatch.tabs.chat'), icon: 'comments' },
      render: () => (
        <TabPane>
          <TeamChatPanel
            contextType="TeamMatch"
            contextId={match.id}
            isMember={isMember}
          />
        </TabPane>
      ),
    }] : []),
    ...(match.status === 'Accepted' || match.status === 'Scheduled' ? [{
      menuItem: { key: 'availability', content: t('teamMatch.tabs.availability'), icon: 'check circle' },
      render: () => (
        <TabPane>
          <TeamMatchAvailabilityPanel match={match} />
        </TabPane>
      ),
    }] : []),
    ...((match.status === 'Accepted' || match.status === 'Scheduled' || match.status === 'Completed') ? [{
      menuItem: { key: 'result', content: t('teamMatch.tabs.result'), icon: 'trophy' },
      render: () => (
        <TabPane>
          <TeamMatchResultPanel match={match} />
        </TabPane>
      ),
    }] : []),
    ...(match.type === 'Open' && match.applications.length > 0 && match.isCurrentUserTeamA ? [{
      menuItem: {
        key: 'applications',
        content: t('teamMatch.tabs.applications'),
        icon: 'envelope open'
      },
      render: () => (
        <TabPane>
          <Header as="h4">{t('teamMatch.applications.title')}</Header>
          {match.applications
            .filter(a => a.status === 'Pending')
            .map(app => (
              <Segment key={app.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Image
                      src={app.teamLogoUrl || '/assets/team-placeholder.png'}
                      avatar style={{ width: 36, height: 36 }}
                    />
                    <strong>{app.teamName}</strong>
                  </div>
                  <Button
                    positive size="small" loading={submitting}
                    onClick={() => acceptApplication(app.id)}
                  >
                    <Icon name="check" />{t('teamMatch.applications.accept')}
                  </Button>
                </div>
              </Segment>
            ))}
          {match.applications.filter(a => a.status === 'Pending').length === 0 && (
            <Message info>
              <Icon name="info circle" />
              {t('teamMatch.applications.noPending')}
            </Message>
          )}
        </TabPane>
      ),
    }] : []),
  ];

  return (
    <Grid stackable>
      <Grid.Column width={16}>
        {/* Header card */}
        <Segment>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            {/* Team A */}
            <div style={{ textAlign: 'center', flex: '1 1 120px', minWidth: 100 }}>
              <Link to={`/teams/${match.teamAId}`}>
                <Image
                  src={match.teamALogoUrl || '/assets/team-placeholder.png'}
                  circular centered
                  style={{ width: 70, height: 70, objectFit: 'cover', border: '2px solid #eee', cursor: 'pointer' }}
                />
                <div style={{ fontWeight: 700, marginTop: 6, color: '#2185d0' }}>{match.teamAName}</div>
              </Link>
            </div>

            {/* VS */}
            <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
              <Label
                color={statusColor[match.status] || 'grey'}
                size="small"
                style={{ marginBottom: 8, display: 'block' }}
              >
                {t(`teamMatch.status.${match.status.toLowerCase()}`)}
              </Label>
              <div style={{ fontSize: '1.4em', fontWeight: 900, color: '#999' }}>VS</div>
              <Label size="mini" basic style={{ marginTop: 6 }}>
                <Icon name={match.type === 'Direct' ? 'lock' : 'unlock'} />
                {t(`teamMatch.type.${match.type.toLowerCase()}`)}
              </Label>
              {match.scheduledAt && (
                <div style={{ fontSize: '0.78em', color: '#888', marginTop: 6 }}>
                  <Icon name="calendar" />
                  {format(new Date(match.scheduledAt), 'dd MMM yyyy, HH:mm')}
                </div>
              )}
            </div>

            {/* Team B */}
            <div style={{ textAlign: 'center', flex: '1 1 120px', minWidth: 100 }}>
              {match.teamBName ? (
                <Link to={`/teams/${match.teamBId}`}>
                  <Image
                    src={match.teamBLogoUrl || '/assets/team-placeholder.png'}
                    circular centered
                    style={{ width: 70, height: 70, objectFit: 'cover', border: '2px solid #eee', cursor: 'pointer' }}
                  />
                  <div style={{ fontWeight: 700, marginTop: 6, color: '#2185d0' }}>{match.teamBName}</div>
                </Link>
              ) : (
                <div style={{ color: '#bbb', textAlign: 'center', padding: '10px 0' }}>
                  <Icon name="question circle" size="large" />
                  <div style={{ fontSize: '0.85em', marginTop: 4 }}>{t('teamMatch.waitingForOpponent')}</div>
                </div>
              )}
            </div>
          </div>

          {/* Actions for Team B captain to respond to direct challenge */}
          {match.status === 'Pending' && match.isCurrentUserTeamB && match.isCurrentUserCaptainOfAnyTeam && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
              <Button positive loading={submitting} onClick={() => respondToChallenge(match.id, true)}>
                <Icon name="check" />{t('teamMatch.acceptChallenge')}
              </Button>
              <Button negative loading={submitting} onClick={() => respondToChallenge(match.id, false)}>
                <Icon name="close" />{t('teamMatch.rejectChallenge')}
              </Button>
            </div>
          )}
        </Segment>

        {/* Tabs */}
        {panes.length > 0 && (
          <Tab
            panes={panes}
            menu={{ secondary: true, pointing: true }}
            style={{ marginTop: 16 }}
            activeIndex={activeTab}
            onTabChange={(_, data) => setActiveTab(data.activeIndex as number)}
          />
        )}
      </Grid.Column>
    </Grid>
  );
});